import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    zeosConfirmation, zeosDialog
} from '../../../components/Shared/confirm-dialog/ConfirmDialog';
import { ZeosInput } from '../../../components/Shared/ZeosInput';
import { ZeosModal } from '../../../components/Shared/ZeosModal';
import { ZeosSignin } from '../../../components/Shared/ZeosSignin';
import { AuthContext } from '../../../context/AuthContext';
import { ZeosApiContext } from '../../../context/ZeosApiContext';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useSignIn } from '../../../hooks/useSignIn';
import { Analytics } from '../../../services/analytics/Analytics';
import { BAD_LOGIN, EMAIL_ALREADY_REGISTERED } from '../../../services/auth/AuthHelpers';
import { REQUEST_ERROR } from '../../../services/zeos-api/ZeosApiExecutor';
import { IZeosUser } from '../../../services/auth/IZeosUser';
import {
    handleZeosEnviromentEmailWhitelist, isDemo
} from '../../../services/environment/EnvironmentHelpers';
import { defaultSignupForm } from '../../../services/forms/DefaultFormData';
import { ISignupFormDataType } from '../../../services/forms/FormDataTypes';
import {
    signupValidationSchema
} from '../../../services/forms/validationSchemas/SignUpValidationSchema';
import { ZeosFontSize } from '../../../services/helpers/ThemeHelpers';
import { createLogger } from '../../../services/Logger';
import { ONBOARDING_BASE_PATH } from '../../../services/nav/Constants';
import { navStepManagerByStepNum } from '../../../services/nav/NavigationService';
import { IUserInit } from '../../../services/zeos-api/contracts/Account';
import { ICustomerRequest } from '../../../services/zeos-api/contracts/Customer';
import { CustomerRoleEnum } from '../../../services/zeos-api/enums/Customer';
import { OnboardStepEnum } from '../../../services/zeos-api/enums/Onboarding';
import { OnboardContainer } from '../layout/OnboardContainer';

export const AccountSignupScreen = () => {
  const log = createLogger("AccountSignupScreen");
  const auth = useContext(AuthContext);
  const api = useContext(ZeosApiContext);
  const navigate = useNavigate();
  const { signOut } = useSignIn();

  const [showSignin, setShowSignin] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const { formData, setFormData, errors, formValidate, formChange } =
    useFormValidation<ISignupFormDataType>(
      signupValidationSchema,
      defaultSignupForm
    );

  useEffect(() => {
    Analytics.capture("onboarding_started");
  }, []);

  useEffect(() => {
    if (!isDemo()) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || "Alan",
      lastName: prev.lastName || "Evans",
      email: prev.email || "alan.evans@rewiregroup.net",
      confirmEmail: prev.confirmEmail || "alan.evans@rewiregroup.net",
    }));
  }, [setFormData]);

  const handleNewUser = async (doNavigate: () => void) => {
    setShowSpinner(true);
    //Test email/username is part of this tier's whitelist
    const checkEmailDomain = handleZeosEnviromentEmailWhitelist(formData.email);
    if (!checkEmailDomain.success) {
      alert(checkEmailDomain.message);
      setShowSpinner(false);
      return;
    }
    // Clear any leftover session/draft state from a previous account before
    // provisioning this one, so a new registration can never inherit another
    // tenant's still-authenticated session or an abandoned draft.
    await signOut();
    //Create ZeosUser, and store info in authContext.newZeosUser
    //NewZeosUser is a manually-set substitute for ZeosUser, which is for signed-in users.
    const user: IUserInit = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailAddress: formData.email,
    };
    const newUserResult = await api.Account.postUserAsync(user);
    const userData = newUserResult.data;
    if (!userData || newUserResult.zeosStatus === "error" || newUserResult.message === REQUEST_ERROR || newUserResult.message === "error") {
      setShowSpinner(false);
      if (newUserResult.message === EMAIL_ALREADY_REGISTERED) {
        // Most often this is the user's own abandoned signup: the address is held
        // by their unverified row, and there is no Firebase account yet to reset a
        // password against. "Failed to create your account" would leave them with
        // nowhere to go, so point them at the step they stopped on.
        await zeosConfirmation(
          "That email address is already in use. If you started signing up and haven’t entered your verification code yet, continue there. Otherwise, sign in instead.",
          "Continue verification",
          async () => {
            navigate(`${ONBOARDING_BASE_PATH}/account-verification`);
          }
        );
        return;
      }
      zeosDialog("Failed to create your account. Please try again.");
      return;
    }
    const newUser: IZeosUser = {
      displayName: `${userData.firstName} ${userData.lastName}`,
      zeosUserId: userData.zeosUserId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      emailAddress: userData.emailAddress,
      authSystemId: "",
      principalId: 0,
      phone: "",
      customerClaims: [],
      summaryEmailSent: userData.summaryEmailSent,
      isSuperAdmin: false,
    };
    auth.setNewZeosUser(newUser);
    //Create this user's customer and update the newZeosUser.customerClaims
    const newCustomer: ICustomerRequest = {
      customerName: `${userData.firstName} ${userData.lastName}`,
      customerTypeId: 0,
      userRoles: [{ zeosUserId: userData.zeosUserId, customerRoleId: CustomerRoleEnum.Admin }],
    };
    const customer = await api.Customer.postCustomerAsync(newCustomer);
    if (!customer.data?.customerId || customer.zeosStatus === "error" || customer.message === REQUEST_ERROR || customer.message === "error") {
      setShowSpinner(false);
      const refMsg = customer.errorRef
        ? ` Please call support with reference number: ${customer.errorRef}.`
        : " Please try again.";
      zeosDialog(`Failed to set up your account.${refMsg}`);
      return;
    }
    newUser.customerClaims = [
      {
        customerId: customer.data.customerId,
        customerName: customer.data.customerName,
        companies: [],
      },
    ];
    auth.setNewZeosUser(newUser);
    //Navigate to next step
    setShowSpinner(false);
    doNavigate();
  };

  const handleNext = async (doNavigate: () => void) => {
    formValidate(() => handleNewUser(doNavigate));
  };

  const handleSigninSuccess = async (user: IZeosUser) => {
    //TODO: Navigation rules need to be in the navigation service
    const onboardStep = await api.Onboarding.getOnboardStep(user.zeosUserId);
    const lastStep = onboardStep.data ?? OnboardStepEnum.NotStarted;
    let nextStepNum;
    switch (lastStep) {
      case OnboardStepEnum.NotStarted:
      case OnboardStepEnum.Account:
        nextStepNum = OnboardStepEnum.Company;
        break;
      case OnboardStepEnum.Summary:
        nextStepNum = lastStep;
        break;
      default:
        nextStepNum = lastStep + 1;
        break;
    }
    const nextPage = navStepManagerByStepNum(nextStepNum);
    if (nextPage) {
      navigate(`${ONBOARDING_BASE_PATH}/${nextPage.slug}`);
    } else {
      log.error(`Next page not found for step: ${nextStepNum}`);
    }
  };

  const loginForm = (
    <>
      <ZeosModal isOpen={showSignin} onClose={() => setShowSignin(false)}>
        <h2
          className={`text-${ZeosFontSize.lg} font-semibold mb-6 text-center`}
        >
          Sign-in to your account
        </h2>
        <ZeosSignin
          showLogo={false}
          showFooter={false}
          buttonLabel="Ok"
          mode="modal"
          resetLinkSource="onboarding"
          onCancel={() => setShowSignin(false)}
          onSuccess={async (user) => handleSigninSuccess(user)}
          onFailure={() => zeosDialog(BAD_LOGIN)}
        />
      </ZeosModal>
      <div className="grid grid-cols-12">
        {/* Sign-Up Section */}
        <div className="col-span-6 px-2">
          <ZeosInput
            name="firstName"
            label="First Name"
            placeholder="Enter First Name"
            value={formData.firstName}
            onChange={formChange("firstName")}
            error={errors.firstName}
            required={true}
          />
        </div>
        <div className="col-span-6 px-2">
          <ZeosInput
            name="lastName"
            label="Last Name"
            placeholder="Enter Last Name"
            value={formData.lastName}
            onChange={formChange("lastName")}
            error={errors.lastName}
            required={true}
          />
        </div>
        <div className="col-span-6 px-2">
          <ZeosInput
            name="email"
            label="Email Address"
            type="email"
            placeholder="Enter Email"
            value={formData.email}
            onChange={formChange("email")}
            error={errors.email}
            required={true}
          />
        </div>
        <div className="col-span-6 px-2">
          <ZeosInput
            name="confirmEmail"
            label="Confirm Email"
            type="email"
            placeholder="Re-enter Email"
            value={formData.confirmEmail}
            onChange={formChange("confirmEmail")}
            error={errors.confirmEmail}
            required={true}
          />
        </div>
        {/* DO NOT DELETE. TO BE REINSTATED. */}

        {/* <div className="col-span-12 mb-6">
          <div className="flex items-center">
            <span className="text-sm text-gray-500">
              Already have an account?{" "}
              <a
                className="text-zeos-orange-dk cursor-pointer"
                onClick={() => setShowSignin(true)}
              >
                Sign In
              </a>
            </span>
          </div>
        </div> */}
      </div>
    </>
  );

  return (
    <div>
      <OnboardContainer
        content={loginForm}
        contentType="form"
        onNext={handleNext}
        pageSlug="account-setup"
        showSpinner={showSpinner}
      />
    </div>
  );
};
